import React, { FC, PropsWithChildren, useLayoutEffect, useRef, useState } from 'react';
import { Platform, View, useWindowDimensions } from 'react-native';
import { cln } from '../../utils/classnames';
import { Icon } from '../index';
import svgIcons from '../../assets';
import { isWeb } from '../../utils/responsive';

interface Props {
  columnContainerRef?: React.RefObject<View>;
  top?: string;
  translateTopToCenter?: boolean;
  tooltipOffset?: string;
  testID?: string;
}

// Reserve the tooltip's worst-case width (max-w-[382px] box + triangle + gaps). We use a
// fixed reserve rather than measuring the live width because the async HTML content isn't
// at its final width when the flip is decided — measuring it can under-report and overflow.
const TOOLTIP_RESERVE = 410;

const Tooltip: FC<PropsWithChildren<Props>> = ({
  children,
  columnContainerRef,
  top = '50%',
  translateTopToCenter = true,
  tooltipOffset = '80%',
  testID,
}) => {
  const tooltipRef = useRef<View>(null);
  const [shouldBeOnLeftSide, setShouldBeOnLeftSide] = useState(false);
  const [isPositionCalculated, setIsPositionCalculated] = useState(false);
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
  const { width: screenWidth } = useWindowDimensions();

  useLayoutEffect(() => {
    const calculatePosition = () => {
      setIsPositionCalculated(false);

      const node = tooltipRef.current;
      if (!node) {
        setIsPositionCalculated(true);
        return;
      }

      // Web: decide the side from the ANCHOR's real viewport rect (a stable reference that
      // doesn't move when the tooltip flips) vs a fixed width reserve. Direct overflow test,
      // so it can't oscillate or push the tooltip off-screen. When neither side has room,
      // pick the roomier one and cap the box width to it so the text wraps taller instead.
      if (isWeb && typeof window !== 'undefined') {
        const el = node as unknown as HTMLElement;
        const anchor = el.parentElement;
        if (!anchor) {
          setIsPositionCalculated(true);
          return;
        }
        const anchorRect = anchor.getBoundingClientRect();
        const rightLimit = columnContainerRef?.current
          ? (columnContainerRef.current as unknown as HTMLElement).getBoundingClientRect().right
          : window.innerWidth;
        const leftLimit = columnContainerRef?.current
          ? (columnContainerRef.current as unknown as HTMLElement).getBoundingClientRect().left
          : 0;
        const spaceRight = rightLimit - anchorRect.right;
        const spaceLeft = anchorRect.left - leftLimit;

        let openLeft;
        if (spaceRight >= TOOLTIP_RESERVE) openLeft = false;
        else if (spaceLeft >= TOOLTIP_RESERVE) openLeft = true;
        else openLeft = spaceLeft > spaceRight;

        const GAP = 28; // triangle + ml-2 + breathing room
        const available = openLeft ? spaceLeft : spaceRight;
        setShouldBeOnLeftSide(openLeft);
        setMaxWidth(Math.min(382, Math.max(200, available - GAP - 20)));
        setIsPositionCalculated(true);
        return;
      }

      // Native fallback: keep the original RN measure()-based behaviour.
      node.measure((_x, _y, width, _height, pageX) => {
        const tooltipRight = pageX + width;

        if (columnContainerRef?.current) {
          columnContainerRef.current.measure((_cx, _cy, containerWidth, _ch, containerPageX) => {
            const containerRight = containerPageX + containerWidth;
            const distanceFromRight = containerRight - tooltipRight;
            setShouldBeOnLeftSide(distanceFromRight <= width + 16);
            setIsPositionCalculated(true);
          });
        } else {
          setShouldBeOnLeftSide(tooltipRight + width + 16 > screenWidth);
          setIsPositionCalculated(true);
        }
      });
    };

    calculatePosition();

    if (Platform.OS === 'web') {
      window.addEventListener('resize', calculatePosition);
      return () => {
        window.removeEventListener('resize', calculatePosition);
        setShouldBeOnLeftSide(false);
      };
    }
  }, [screenWidth, columnContainerRef]);

  const triangleLeft = (
    <Icon
      icon={svgIcons.trianglePointerIcon}
      mobileSize={{ width: 12, height: 12 }}
      webSize={{ width: 12, height: 12 }}
    />
  );

  const triangleRight = (
    <Icon
      icon={svgIcons.trianglePointerIcon}
      mobileSize={{ width: 12, height: 12 }}
      webSize={{ width: 12, height: 12 }}
      classNames="rotate-180"
    />
  );

  return (
    <View
      ref={tooltipRef}
      testID={testID}
      className={cln(
        'absolute items-center z-20',
        shouldBeOnLeftSide ? 'flex-row-reverse' : 'flex-row',
        !isPositionCalculated && 'invisible',
        !columnContainerRef?.current && 'ml-2',
      )}
      style={
        {
          top,
          transform: translateTopToCenter ? 'translate(0, -50%)' : 'none',
          opacity: isPositionCalculated ? 1 : 0,
          ...(shouldBeOnLeftSide ? { right: tooltipOffset } : { left: tooltipOffset }),
        } as object
      }
    >
      <View className="min-w-3 min-h-3">{shouldBeOnLeftSide ? triangleRight : triangleLeft}</View>
      <View
        className={cln(
          !isPositionCalculated && 'visible',
          'p-2 bg-neutral-910 dark:bg-neutral-100',
        )}
        style={maxWidth ? ({ maxWidth } as object) : undefined}
      >
        {children}
      </View>
    </View>
  );
};

export default Tooltip;
